import { useSuspenseQuery } from '@tanstack/react-query';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Alert, LoadingIndicator } from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import { type ImportRunWithTrace, useSiteImportRuns } from '../../../Components';
import { apiQuery } from '../../../state';

/**
 * Banner showing the status of the import on a site page.
 */
export function SiteImportBanner(props: { site: api.Site; organization: api.Organization }) {
    const { site, organization } = props;
    const importRuns = useSiteImportRuns(site.id);
    // On a site, the import is run from the empty state, there is only one import at a time.
    const run = importRuns[0];

    if (!run) {
        return null;
    }

    const changeRequestId =
        'changeRequest' in run.trace.target ? run.trace.target.changeRequest : undefined;

    assert(changeRequestId, 'Site import should always be in a change request');

    return (
        <SiteImportAlert
            organization={organization}
            changeRequestId={changeRequestId}
            importRun={run}
        />
    );
}

function SiteImportAlert(props: {
    changeRequestId: string;
    importRun: ImportRunWithTrace;
    organization: api.Organization;
}) {
    const { importRun, changeRequestId } = props;
    const { data: changeRequest } = useSuspenseQuery(
        apiQuery('getChangeRequestById', {
            spaceId: importRun.trace.target.space,
            changeRequestId: changeRequestId,
        })
    );

    if (
        changeRequest.status === api.ChangeRequestStatus.Merged ||
        changeRequest.status === api.ChangeRequestStatus.Archived
    ) {
        return null;
    }

    const action = {
        label: 'View change request',
        kind: 'secondary' as const,
        href: {
            screen: {
                type: 'space' as const,
                organization: props.organization.id,
                space: importRun.trace.target.space,
                page: '',
                changeRequest: changeRequestId,
            },
        },
        onPress: () => {
            analytics.logEvent({
                type: 'import_open',
                trigger: 'site_overview',
                status: importRun.status,
            });
        },
    };

    switch (importRun.status) {
        case 'pending':
        case 'in-progress':
            return (
                <Alert
                    size="small"
                    icon={<LoadingIndicator />}
                    title="An import is running"
                    details="Your site is being imported. This may take a few minutes."
                    action={action}
                />
            );
        case 'completed': {
            return (
                <Alert
                    size="small"
                    kind="success"
                    title="Your import is complete"
                    details="One last step: review and publish your changes."
                    action={action}
                />
            );
        }
        case 'cancelled':
        case 'failed': {
            return null;
        }
        default:
            assertNever(importRun.status);
    }
}
